<template>
  <div class="content">
    <v-card :loading="!!loading">
      <v-card-title>Data Validation</v-card-title>
      <v-card-text>
        <div class="record">
          <div class="key">New Records:</div>
          <div class="value">{{ newCount }}</div>
        </div>
        <div class="record">
          <div class="key">Approved Records:</div>
          <div class="value">{{ approvedCount }}</div>
        </div>
        <div class="record">
          <div class="key">Total Records:</div>
          <div :class="{ok: approvedOk, bad: !approvedOk}" class="value">
            {{ newCount }} + {{ approvedCount }}
            {{ approvedOk ? '==' : '!=' }}
            {{ totalCount }}
            <v-icon v-if="approvedOk" color="green">mdi-check-bold</v-icon>
            <v-icon v-if="!approvedOk" color="red">mdi-alert</v-icon>
          </div>
        </div>

        <div class="sep"></div>
        <div class="record">
          <div class="key">Deleted Records:</div>
          <div class="value">{{ deletedCount }}</div>
        </div>
        <div class="record">
          <div class="key">Non-Deleted Records:</div>
          <div class="value">{{ nonDeletedCount }}</div>
        </div>
        <div class="record">
          <div class="key">Total Records:</div>
          <div :class="{ok: totalOk, bad: !totalOk}" class="value">
            {{ deletedCount }} + {{ nonDeletedCount }}
            {{ totalOk ? '==' : '!=' }}
            {{ totalCount }}
            <v-icon v-if="totalOk" color="green">mdi-check-bold</v-icon>
            <v-icon v-if="!totalOk" color="red">mdi-alert</v-icon>
          </div>
        </div>
        <div class="sep"></div>
        <div class="record">
          <div class="key">Deleted & Approved Records:</div>
          <div :class="{ok: !deletedApprovedCount, bad: deletedApprovedCount}" class="value">
            {{ deletedApprovedCount }}
            <v-icon v-if="!deletedApprovedCount" color="green">mdi-check-bold</v-icon>
            <v-icon v-if="deletedApprovedCount" color="red">mdi-alert</v-icon>
          </div>
        </div>
        <div class="button-container">
          <v-btn :disabled="!!loading" @click="fixData()">Fix missing fields</v-btn>
        </div>
        <v-card v-if="fixed">
          <v-card-text>
            <div class="record">
              <div class="key">Fixed deleted:</div>
              <div class="value">{{ fixed.deleted }}</div>
            </div>
            <div class="record">
              <div class="key">Fixed created:</div>
              <div class="value">{{ fixed.created }}</div>
            </div>
            <div class="record">
              <div class="key">Fixed approved:</div>
              <div class="value">{{ fixed.approved }}</div>
            </div>
            <div class="record">
              <div class="key">Fixed uploaded:</div>
              <div class="value">{{ fixed.uploaded }}</div>
            </div>
          </v-card-text>
        </v-card>
      </v-card-text>
    </v-card>
  </div>
</template>
<script>
import { collection, deleteField, doc, getDocs, getFirestore, query, setDoc, where } from 'firebase/firestore';

export default {
  data: () => ({
    totalCount: 0,
    deletedCount: 0,
    nonDeletedCount: 0,
    deletedApprovedCount: 0,
    newCount: 0,
    approvedCount: 0,
    fixed: null,
    loading: 0,
  }),
  computed: {
    totalOk() {
      return this.nonDeletedCount + this.deletedCount === this.totalCount;
    },
    approvedOk() {
      return this.newCount + this.approvedCount === this.totalCount;
    },
  },
  async mounted() {
    await this.loadStats();
  },
  methods: {
    async task(runnable) {
      this.loading++;
      try {
        await runnable();
      } finally {
        this.loading--;
      }
    },
    async loadStats() {
      const db = getFirestore();
      this.task(async () => {
        this.totalCount = (await getDocs(query(collection(db, 'receipts')))).size;
      }).then();
      this.task(async () => {
        this.nonDeletedCount = (await getDocs(query(collection(db, 'receipts'), where('deleted', '==', null)))).size;
      }).then();
      this.task(async () => {
        const deletedDocs = await getDocs(query(collection(db, 'receipts'), where('deleted', '!=', null)));
        this.deletedCount = deletedDocs.size;
        this.deletedApprovedCount = 0;
        deletedDocs.forEach(doc => {
          if (doc.data().approved) {
            this.deletedApprovedCount++;
          }
        });
      }).then();
      this.task(async () => {
        this.newCount = (await getDocs(query(collection(db, 'receipts'), where('approved', '==', null)))).size;
      }).then();
      this.task(async () => {
        this.approvedCount = (await getDocs(query(collection(db, 'receipts'), where('approved', '!=', null)))).size;
      }).then();
    },
    async fixData() {
      await this.task(async () => {
        const fixed = {
          deleted: 0,
          created: 0,
          approved: 0,
          uploaded: 0,
        };
        const db = getFirestore();
        const results = await getDocs(query(collection(db, 'receipts')));
        for (const receipt of results.docs) {
          const data = receipt.data();
          if (data.deleted === undefined) {
            console.info(`fixed deleted for ${receipt.id}`);
            const receiptRef = doc(db, 'receipts', receipt.id);
            await setDoc(receiptRef, { deleted: null }, { merge: true });
            fixed.deleted++;
          }
          if (data.created === undefined) {
            console.info(`fixed created for ${receipt.id}`);
            const receiptRef = doc(db, 'receipts', receipt.id);
            await setDoc(receiptRef, { created: new Date() }, { merge: true });
            fixed.created++;
          }
          if (data.approved === undefined) {
            console.info(`fixed approved for ${receipt.id}`);
            const receiptRef = doc(db, 'receipts', receipt.id);
            await setDoc(receiptRef, { approved: null }, { merge: true });
            fixed.approved++;
          }
          if (data.uploaded) {
            console.info(`fixed uploaded for ${receipt.id}`);
            const receiptRef = doc(db, 'receipts', receipt.id);
            await setDoc(receiptRef, { uploaded: deleteField() }, { merge: true });
            fixed.uploaded++;
          }
        }
        this.fixed = fixed;
      });
      await this.loadStats();
    },
  },
};
</script>
<style lang="scss">
.record {
  display: table-row;

  .key, .value {
    display: table-cell;
    padding: 0 1rem;
  }

  .value {
    font-weight: bold;
  }
}

.button-container {
  margin: 1rem 0;
}

.bad {
  color: red;
}

.ok {
  color: green;
}

.sep {
  display: table-row;
  height: 1rem;
}

.v-card--loading .value {
  opacity: 0.1;
}
</style>
